import {
  forwardRef,
  useId,
  useState,
  type ReactNode,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
} from 'react';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

type CommonProps = {
  label: string;
  hideLabel?: boolean;
  helperText?: string;
  errorMessage?: string | null;
  leadingIcon?: ReactNode;
  trailingAddon?: ReactNode;
  required?: boolean;
};

type TextFieldProps = CommonProps &
  InputHTMLAttributes<HTMLInputElement> & {
    id?: string;
    className?: string;
  };

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    hideLabel,
    helperText,
    errorMessage,
    leadingIcon,
    trailingAddon,
    required,
    className,
    id: idProp,
    type,
    disabled,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const helperId = helperText ? `${id}-help` : undefined;
  const errorId = errorMessage ? `${id}-err` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;
  const hasError = !!errorMessage;
  const isPassword = type === 'password';
  const [passwordVisible, setPasswordVisible] = useState(false);
  const resolvedType = isPassword ? (passwordVisible ? 'text' : 'password') : type;
  const passwordToggle =
    isPassword && !trailingAddon ? (
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onClick={() => setPasswordVisible((v) => !v)}
        aria-label={passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        aria-pressed={passwordVisible}
        className="flex h-9 w-9 items-center justify-center rounded-md text-neutral-400 hover:text-neutral-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {passwordVisible ? (
          <EyeOff className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Eye className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    ) : null;
  const resolvedTrailing = trailingAddon ?? passwordToggle;
  const hasTrailing = Boolean(resolvedTrailing);

  return (
    <div className="flex w-full flex-col gap-1.5">
      <label
        htmlFor={id}
        className={hideLabel ? 'sr-only' : 'text-xs font-medium text-neutral-700'}
      >
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      <div className="relative">
        {leadingIcon && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-400"
          >
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          type={resolvedType}
          required={required}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          className={
            (leadingIcon ? 'pl-9 ' : 'pl-3 ') +
            (hasTrailing ? 'pr-12 ' : 'pr-3 ') +
            'bg-neutral-0 h-11 w-full rounded-md border py-2 text-sm text-neutral-900 transition-colors placeholder:text-neutral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500 ' +
            (hasError ? 'border-danger' : 'border-neutral-300 hover:border-neutral-400') +
            (className ? ` ${className}` : '')
          }
          {...rest}
        />
        {resolvedTrailing && (
          <span className="absolute top-1/2 right-2 -translate-y-1/2">{resolvedTrailing}</span>
        )}
      </div>
      {helperText && !errorMessage && (
        <p id={helperId} className="text-xs text-neutral-500">
          {helperText}
        </p>
      )}
      {errorMessage && (
        <p id={errorId} className="text-danger flex items-center gap-1 text-xs" role="alert">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{errorMessage}</span>
        </p>
      )}
    </div>
  );
});

type SelectFieldProps = CommonProps &
  SelectHTMLAttributes<HTMLSelectElement> & {
    id?: string;
    className?: string;
  };

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  {
    label,
    hideLabel,
    helperText,
    errorMessage,
    children,
    required,
    className,
    id: idProp,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const helperId = helperText ? `${id}-help` : undefined;
  const errorId = errorMessage ? `${id}-err` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;
  const hasError = !!errorMessage;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <label
        htmlFor={id}
        className={hideLabel ? 'sr-only' : 'text-xs font-medium text-neutral-700'}
      >
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      <select
        ref={ref}
        id={id}
        required={required}
        aria-invalid={hasError}
        aria-describedby={describedBy}
        className={
          'bg-neutral-0 h-11 w-full rounded-md border px-3 py-2 text-sm text-neutral-900 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500 ' +
          (hasError ? 'border-danger' : 'border-neutral-300 hover:border-neutral-400') +
          (className ? ` ${className}` : '')
        }
        {...rest}
      >
        {children}
      </select>
      {helperText && !errorMessage && (
        <p id={helperId} className="text-xs text-neutral-500">
          {helperText}
        </p>
      )}
      {errorMessage && (
        <p id={errorId} className="text-danger flex items-center gap-1 text-xs" role="alert">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{errorMessage}</span>
        </p>
      )}
    </div>
  );
});
